import { isValidTimeWindowSelection } from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.selection";
import { getCurrentSearchParamsObj } from "src/hooks/useSearchParams";
import { URL_ARRAY_SEPARATOR } from "src/utils/constants";
import { create, type StoreApi } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";
import type { TimeWindow } from "@components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.types";
import { useGlobalLocalContext } from "./localStorage";
import {
  type ENV_STORAGE_TYPE,
  INITIAL_SESSION_STATE,
  INITIAL_TIME_WINDOW,
  SEARCH_STORAGE_VERSION,
  STORAGE_KEY,
  URL_SCHEMA,
  type URL_SCHEMA_TYPE,
  ZUSTAND_SCHEMA,
} from "./useSearchStorage.constants";
import type { SchemaKeys, Setters } from "./useSearchStorage.types";

// Cannot be moved to the constants file because it has a `SchemaKeys` type which would cause a circular dependency.
// Exported for use in env switching (Topbar.hooks.ts) to clear URL params before rehydrating.
export const URL_STORABLE_KEYS = Object.keys(URL_SCHEMA.shape) as SchemaKeys[];

// Flag to prevent pushState during popstate handling
let isHandlingPopState = false;

// Flag to prevent pushState during env switching (avoids race condition with React Router navigation)
let isSwitchingEnv = false;

/**
 * Sets the env switching flag. When true, setItem() skips pushState to avoid
 * interfering with React Router navigation during env switch.
 */
export const setIsSwitchingEnv = (value: boolean): void => {
  isSwitchingEnv = value;
};

/** True while `switchEnv` has set the next env but has not finished navigating. */
export const getIsSwitchingEnv = (): boolean => isSwitchingEnv;

/**
 * Lazily reads the current env from localStorage store.
 * Returns empty string if env is not set (e.g., on auth routes).
 */
const getCurrentEnv = (): string => useGlobalLocalContext.getState().env;

/**
 * Reads the nested env storage from sessionStorage or localStorage.
 * Returns an object keyed by env name, with each env having its own state.
 */
const readNestedStorage = (key: string): ENV_STORAGE_TYPE => {
  const sessionValue = window.sessionStorage.getItem(key) ?? "";
  const storageValue = window.localStorage.getItem(key) ?? "";

  // Try to parse as new nested format first
  const sessionParsed = sessionValue ? JSON.parse(sessionValue) : { state: {} };
  const storageParsed = storageValue ? JSON.parse(storageValue) : { state: {} };

  // Check if it's the new nested format (state is an object with env keys containing objects)
  // vs old flat format (state has timeWindow, appOwners, appTags directly)
  const isNestedFormat = (state: any): boolean => {
    if (!state || typeof state !== "object") return false;
    // If state has any of the flat keys directly, it's old format
    if ("timeWindow" in state || "appOwners" in state || "appTags" in state) {
      return false;
    }
    return true;
  };

  const sessionState = isNestedFormat(sessionParsed.state)
    ? sessionParsed.state
    : {};
  const storageState = isNestedFormat(storageParsed.state)
    ? storageParsed.state
    : {};

  // Merge localStorage and sessionStorage (sessionStorage takes priority)
  return { ...storageState, ...sessionState };
};

/**
 * Writes the nested env storage to both sessionStorage and localStorage.
 */
const writeNestedStorage = (key: string, nestedState: ENV_STORAGE_TYPE): void => {
  const stringified = JSON.stringify({ state: nestedState });
  window.sessionStorage.setItem(key, stringified);
  window.localStorage.setItem(key, stringified);
};

// A `timeWindow` from the URL or from storage written by an older build can be
// anything: a minute count, a hand-edited token, or garbage. Only selections the
// parser recognises are kept; everything else falls back to INITIAL_TIME_WINDOW.
export const isInvalidTimeWindow = (value: unknown): boolean =>
  !isValidTimeWindowSelection(value);

const hashStorage: StateStorage = {
  /**
   * Stores flat values from Zustand into nested per-env structure.
   * Also syncs current env's values to URL (unless handling popstate).
   * No-op if env is not set (e.g., on auth routes).
   */
  setItem: (storageKey, newValue): void => {
    const currentEnv = getCurrentEnv();

    // Skip storage if no env is set (e.g., auth routes like /login, /signup)
    if (!currentEnv) return;

    const parsed = ZUSTAND_SCHEMA.parse(JSON.parse(newValue));
    const flatState = parsed.state;

    // Read existing nested storage and update current env's slot
    const nestedStorage = readNestedStorage(storageKey);
    nestedStorage[currentEnv] = flatState;

    // Write nested structure to sessionStorage/localStorage
    writeNestedStorage(storageKey, nestedStorage);

    // Skip URL update during popstate handling or env switching to prevent history disruption
    if (!isHandlingPopState && !isSwitchingEnv) {
      const url = new URL(window.location as unknown as string);
      const parsedEntries = Object.entries(flatState);

      // Update URL search params with current env's values
      for (let i = 0; i < parsedEntries.length; i++) {
        const statePropKey = parsedEntries[
          i
        ][0] as keyof typeof INITIAL_SESSION_STATE;
        const value = parsedEntries[i][1];

        if (
          value === null ||
          (Array.isArray(value) && value.length === 0) ||
          (statePropKey !== "timeWindow" &&
            value === INITIAL_SESSION_STATE[statePropKey])
        ) {
          url.searchParams.delete(statePropKey);
        } else {
          // Use primary separator for arrays to avoid URL encoding issues
          const serializedValue = Array.isArray(value)
            ? value.join(URL_ARRAY_SEPARATOR)
            : value + "";
          url.searchParams.set(statePropKey, serializedValue);
        }
      }

      window.history.pushState({}, "", url);
    }
  },

  /**
   * Reads nested per-env storage and returns flat values for current env.
   * Merges with priority: URL > sessionStorage > localStorage > defaults.
   * If env is not set, returns defaults merged with URL params (no storage write).
   */
  getItem: (key): string => {
    const currentEnv = getCurrentEnv();

    // Read URL params (flat) - these represent current env's values
    const urlStore = { state: {} } as any;
    for (let i = 0; i < URL_STORABLE_KEYS.length; i++) {
      const value = new URLSearchParams(window.location.search).get(
        URL_STORABLE_KEYS[i]
      );
      if (value) {
        urlStore.state[URL_STORABLE_KEYS[i]] = value;
      }
    }
    const urlStorageObj = ZUSTAND_SCHEMA.safeParse(urlStore);

    // Validate URL timeWindow values
    let urlState: any = {};
    if (!urlStorageObj.error) {
      urlState = { ...urlStorageObj.data.state };

      const timeWindowUrlValue = urlState.timeWindow;
      if ("timeWindow" in urlState && isInvalidTimeWindow(timeWindowUrlValue)) {
        urlState.timeWindow = INITIAL_TIME_WINDOW;
      }
    }

    // If no env is set (e.g., auth routes), return defaults + URL params without storage
    if (!currentEnv) {
      const mergedStorage: URL_SCHEMA_TYPE = {
        ...INITIAL_SESSION_STATE,
        ...urlState,
      };
      return JSON.stringify({ state: mergedStorage });
    }

    // Read nested storage and extract current env's values
    const nestedStorage = readNestedStorage(key);
    const envStorage = nestedStorage[currentEnv] ?? {};

    // Validate stored timeWindow values
    if ("timeWindow" in envStorage && isInvalidTimeWindow(envStorage.timeWindow)) {
      envStorage.timeWindow = INITIAL_TIME_WINDOW;
    }

    // Merge with priority: URL > envStorage > defaults
    const mergedStorage: URL_SCHEMA_TYPE = {
      ...INITIAL_SESSION_STATE,
      ...envStorage,
      ...urlState,
    };

    // Update nested storage with merged values for current env
    nestedStorage[currentEnv] = mergedStorage;
    writeNestedStorage(key, nestedStorage);

    // Return flat structure for Zustand (the external API stays unchanged)
    return JSON.stringify({ state: mergedStorage });
  },

  removeItem: (key): void => {
    const search = new URLSearchParams(window.location.search);
    search.delete(key);
    window.location.search = search.toString();
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
  },
};

const generateGetters = (get: () => URL_SCHEMA_TYPE) => ({
  values: () =>
    // pick
    Object.fromEntries(
      Object.keys(URL_SCHEMA.shape).map((key) => [
        key,
        get()[key as keyof typeof get],
      ])
    ),
});

const generateSetters = (
  set: StoreApi<URL_SCHEMA_TYPE & Setters>["setState"]
): Setters => ({
  setTimeWindow: (timeWindow?: TimeWindow) => {
    return set({ timeWindow });
  },
  setAppOwners: (appOwners?: number[]) => set({ appOwners }),
  setAppTags: (appTags?: number[]) => set({ appTags }),
});

type State = typeof INITIAL_SESSION_STATE &
  ReturnType<typeof generateSetters> &
  ReturnType<typeof generateGetters>;

export const useSearchStorage = create<State>()(
  persist(
    (set, get) => {
      const initialState = {
        ...INITIAL_SESSION_STATE,
        ...generateSetters(set),
        ...generateGetters(get),
      };

      const timeWidowValue = getTimeWindowValueFromURL();

      if (timeWidowValue) {
        initialState.timeWindow = timeWidowValue;
      }

      return initialState;
    },
    {
      name: STORAGE_KEY,
      version: SEARCH_STORAGE_VERSION,
      storage: createJSONStorage(() => hashStorage),
    }
  )
);

export function getTimeWindowValueFromURL(): string | undefined {
  const timeWindowSearchParamValue = getCurrentSearchParamsObj()?.timeWindow;
  if (typeof timeWindowSearchParamValue !== "string") {
    return undefined;
  }
  return isValidTimeWindowSelection(timeWindowSearchParamValue)
    ? timeWindowSearchParamValue
    : undefined;
}

/**
 * Parses URL search parameters and returns validated state object.
 * Uses the same validation logic as getItem() to ensure consistency.
 * @returns Validated state object with URL values merged with initial state defaults
 */
function parseUrlStateOnly(): URL_SCHEMA_TYPE {
  const urlStore = { state: {} } as any;
  for (let i = 0; i < URL_STORABLE_KEYS.length; i++) {
    const value = new URLSearchParams(window.location.search).get(
      URL_STORABLE_KEYS[i]
    );
    if (value) {
      urlStore.state[URL_STORABLE_KEYS[i]] = value;
    }
  }

  const urlStorageObj = ZUSTAND_SCHEMA.safeParse(urlStore);

  let urlState: any = {};
  if (!urlStorageObj.error) {
    urlState = { ...urlStorageObj.data.state };

    // Validate timeWindow values - same logic as getItem()
    const timeWindowUrlValue = urlState.timeWindow;
    if ("timeWindow" in urlState && isInvalidTimeWindow(timeWindowUrlValue)) {
      urlState.timeWindow = INITIAL_TIME_WINDOW;
    }
  }

  // Merge with initial state to ensure all keys have values
  return {
    ...INITIAL_SESSION_STATE,
    ...urlState,
  };
}

// Add popstate listener to update state when browser navigation occurs.
// This syncs URL state changes (browser back/forward) with the per-env storage.
window.addEventListener("popstate", () => {
  const rawEnv = useGlobalLocalContext.getState().env;

  // Skip storage sync on routes where env isn't set (e.g., /login, /signup, /logout).
  // These auth routes exist outside the env-based routing and don't use search storage.
  if (!rawEnv) return;

  try {
    // Set flag to prevent URL updates during popstate handling
    isHandlingPopState = true;

    const currentEnv = rawEnv;

    // During browser history navigation, URL is the source of truth
    // Parse URL state WITHOUT merging with localStorage/sessionStorage
    const urlState = parseUrlStateOnly();

    // Update Zustand store to match URL exactly
    useSearchStorage.setState(
      (currentState) => ({
        ...currentState,
        ...urlState,
      }),
      false // Don't replace - preserve setter functions
    );

    // Update current env's slot in nested storage to match the URL state
    const nestedStorage = readNestedStorage(STORAGE_KEY);
    nestedStorage[currentEnv] = urlState;
    writeNestedStorage(STORAGE_KEY, nestedStorage);
  } finally {
    // Reset flag when popstate handling is complete
    isHandlingPopState = false;
  }
});
